import { OwnerModelDTO, OwnerOptionDTO, OwnerTypeDTO } from '../api/useIncomeQuery';

const formatAgeLabel = (year: number, birthYear: number | null): string | undefined =>
  birthYear != null ? `Age ${year - birthYear}` : undefined;

const isJointOwner = (owner: OwnerModelDTO) =>
  owner.OwnerType === OwnerTypeDTO.Joint || owner.Name === 'Joint';

const showsAgeLabel = (owner: OwnerModelDTO) =>
  owner.OwnerType === OwnerTypeDTO.Principal ||
  owner.OwnerType === OwnerTypeDTO.Spouse ||
  isJointOwner(owner);

/**
 * Resolves the owner used for year-dropdown age labels:
 * 1. The owner with the largest ownership percentage
 * 2. If tied, the first owner listed in Profile > Personal Info
 */
export const getDominantOwnerForAgeLabel = (
  ownerModel: OwnerModelDTO[],
  personalInfoOwners: OwnerOptionDTO[],
): OwnerModelDTO | null => {
  if (!ownerModel?.length) {
    return null;
  }

  if (ownerModel.length === 1) {
    return ownerModel[0];
  }

  const maxPercentage = Math.max(...ownerModel.map(owner => owner.Percentage));
  const tiedOwners = ownerModel.filter(owner => owner.Percentage === maxPercentage);

  if (tiedOwners.length === 1) {
    return tiedOwners[0];
  }

  for (const personalInfoOwner of personalInfoOwners) {
    const matchedOwner = tiedOwners.find(owner => owner.Name === personalInfoOwner.Name);
    if (matchedOwner) {
      return matchedOwner;
    }
  }

  return tiedOwners[0];
};

export const getYearAgeRightLabel = (
  year: number,
  ownerModel: OwnerModelDTO[],
  personalInfoOwners: OwnerOptionDTO[],
  primaryYob: number | null,
  spouseYob: number | null,
): string | undefined => {
  if (!Number.isInteger(year)) {
    return undefined;
  }

  const dominantOwner = getDominantOwnerForAgeLabel(ownerModel, personalInfoOwners);
  if (!dominantOwner || !showsAgeLabel(dominantOwner)) {
    return undefined;
  }

  if (isJointOwner(dominantOwner)) {
    const labels = [primaryYob, spouseYob]
      .filter((yob): yob is number => yob != null)
      .map(yob => `Age ${year - yob}`);

    return labels.length > 0 ? labels.join(', ') : undefined;
  }

  const birthYear =
    dominantOwner.OwnerType === OwnerTypeDTO.Principal ? primaryYob : spouseYob;

  return formatAgeLabel(year, birthYear);
};
